import { PublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { CalculationService, DocumentService, LookupService } from '@insight2profit/api-connector';
import { RuntimeEnvironment } from 'startup/getRuntimeEnvironment';

export class InsightServiceLibrary {
  private readonly lookupService: LookupService;
  private readonly calculationService: CalculationService;
  private readonly documentService: DocumentService;
  private readonly msalInstance: PublicClientApplication;

  // these scopes have Azure B2C urls added to them in the constructor
  private authScopes = {
    ces: ['ces.access'],
    lookup: ['lookup.access'],
    docs: ['docs.access'],
  };

  getAllScopes() {
    return Object.values(this.authScopes).flat();
  }

  private bearerTokens = {
    ces: '',
    lookup: '',
    docs: '',
  };

  constructor({
    runtimeEnvironment,
    clientApp,
  }: {
    runtimeEnvironment: RuntimeEnvironment;
    clientApp: PublicClientApplication;
  }) {
    this.authScopes.ces = this.authScopes.ces.map(
      scope => `${runtimeEnvironment.b2cDomainHost}/${runtimeEnvironment.cesAppId}/${scope}`
    );

    this.authScopes.lookup = this.authScopes.lookup.map(
      scope => `${runtimeEnvironment.b2cDomainHost}/${runtimeEnvironment.lookupAppId}/${scope}`
    );

    this.authScopes.docs = this.authScopes.docs.map(
      scope => `${runtimeEnvironment.b2cDomainHost}/${runtimeEnvironment.docsAppId}/${scope}`
    );

    this.msalInstance = clientApp;

    this.calculationService = new CalculationService(runtimeEnvironment.apiURL);
    this.calculationService.unvalidatedClient.setRequestHeadersHandler(this.cesRequestHeadersHandler);
    this.performModelCalculation = this.wrapCesRequestWithToken(this.calculationService.performModelCalculation);

    this.lookupService = new LookupService(runtimeEnvironment.apiURL);
    this.lookupService.unvalidatedClient.setRequestHeadersHandler(this.lookupRequestHeadersHandler);
    this.getLookupTableMembers = this.wrapLookupRequestWithToken(this.lookupService.getTableMembers);
    this.getTableMembersWithFilters = this.wrapLookupRequestWithToken(this.lookupService.getTableMembersWithFilters);

    this.documentService = new DocumentService(runtimeEnvironment.apiURL);
    this.documentService.unvalidatedClient.setRequestHeadersHandler(this.docsRequestHeadersHandler);
    this.getDocumentsByType = this.wrapDocumentRequestWithToken(this.documentService.getDocumentsByType);
    this.getDocumentsByQuery = this.wrapDocumentRequestWithToken(this.documentService.getDocumentsByQuery);
    this.saveDocument = this.wrapDocumentRequestWithToken(this.documentService.saveDocument);
    this.getDocument = this.wrapDocumentRequestWithToken(this.documentService.getDocument);
    this.updateDocument = this.wrapDocumentRequestWithToken(this.documentService.updateDocument);
  }

  private async getToken(service: 'ces' | 'lookup' | 'docs') {
    const tokenRequest: SilentRequest = {
      scopes: this.authScopes[service],
    };
    const authResponse = await this.msalInstance.acquireTokenSilent(tokenRequest);

    return authResponse.accessToken;
  }

  private wrapServiceRequestWithToken<T extends (...stuff: any[]) => any>(
    service: 'ces' | 'lookup' | 'docs',
    request: T
  ) {
    return async (...stuff: Parameters<T>) => {
      const bearerToken = await this.getToken(service);
      this.bearerTokens[service] = bearerToken;
      return request(...stuff);
    };
  }

  private wrapCesRequestWithToken(request: (...stuff: any[]) => Promise<any>) {
    return this.wrapServiceRequestWithToken('ces', request);
  }

  private wrapLookupRequestWithToken(request: (...stuff: any[]) => Promise<any>) {
    return this.wrapServiceRequestWithToken('lookup', request);
  }

  private wrapDocumentRequestWithToken(request: (...stuff: any[]) => Promise<any>) {
    return this.wrapServiceRequestWithToken('docs', request);
  }

  private lookupRequestHeadersHandler = (headers: {}) => ({
    ...headers,
    Authorization: `Bearer ${this.bearerTokens.lookup}`,
  });

  private cesRequestHeadersHandler = (headers: {}) => ({
    ...headers,
    Authorization: `Bearer ${this.bearerTokens.ces}`,
  });

  private docsRequestHeadersHandler = (headers: {}) => ({
    ...headers,
    Authorization: `Bearer ${this.bearerTokens.docs}`,
  });

  getLookupTableMembers: LookupService['getTableMembers'];
  getTableMembersWithFilters: LookupService['getTableMembersWithFilters'];
  performModelCalculation: CalculationService['performModelCalculation'];
  getDocumentsByType: DocumentService['getDocumentsByType'];
  saveDocument: DocumentService['saveDocument'];
  getDocument: DocumentService['getDocument'];
  getDocumentsByQuery: DocumentService['getDocumentsByQuery'];
  updateDocument: DocumentService['updateDocument'];
}
